export const enAppStudio = {
  title: "App Studio",
  subtitle: "Build and manage your visualization apps.",
  empty: "No apps yet. Create your first app to get started.",
  actions: {
    newApp: "New App",
  },
  queryBuilder: {
    description:
      "Pick metrics, dimensions and an optional time range to generate a SQL query.",
    loading: "Loading available fields…",
    empty:
      "This app's data source doesn't have a semantic model yet, so the form builder isn't available. Use the Query tab to write SQL directly.",
    fields: {
      metrics: "Metrics & measures",
      dimensions: "Dimensions",
      timeRange: "Filter by time range",
      startDate: "Start date",
      endDate: "End date",
      grain: "Group by",
    },
    grainNone: "No time grouping",
    grainOptions: {
      day: "Day",
      week: "Week",
      month: "Month",
      quarter: "Quarter",
      year: "Year",
    },
    generate: "Generate SQL",
    generating: "Generating…",
    generatedSqlLabel: "Generated SQL",
    errors: {
      loadFailed: "Could not load available fields. Please try again.",
      generateFailed: "Could not generate the query. Please try again.",
    },
  },
  tabFilters: {
    title: "Filters",
    addFilter: "Add filter",
    clearAll: "Clear all",
    apply: "Apply",
    done: "Done",
    anyValue: "Any",
    valuePlaceholder: "Select values",
    freeTextPlaceholder: "Type a value and press Enter",
    startDate: "Start",
    endDate: "End",
    notFilteredBy: "Not filtered by {{dimensions}}",
    unappliedChanges: "Unapplied changes — click Apply to update the widgets",
  },
  section: {
    toggleAriaLabel: "Toggle {{title}} section",
  },
  list: {
    ariaLabel: "Apps",
    unknownCategory: "Uncategorized",
    loadFailed: "Could not load apps. Please try again.",
  },
  card: {
    moreActionsAriaLabel: "More actions",
    delete: "Delete",
    share: "Share",
  },
  shareDialog: {
    heading: 'Share "{{name}}"',
    subheading:
      "Anyone with the link can view this app without signing in. Choose when the link stops working.",
    oneDay: "1 day",
    sevenDays: "7 days",
    thirtyDays: "30 days",
    never: "Never expires",
    custom: "Custom date and time",
    customLabel: "Expires at",
    customHelper: "Interpreted in your current timezone.",
    linkLabel: "Shareable link",
    linkHelper: "Copy it now — this link is shown only once.",
    expiresOn: "Expires {{when}}",
    neverExpires: "This link never expires.",
    generate: "Generate link",
    cancel: "Cancel",
    done: "Done",
    // Shown only for apps that have been captured at least once — an app with no snapshots has
    // nothing to choose between.
    viewMode: {
      label: "What should this link show?",
      live: "Live data only",
      liveAndSnapshots: "Live data + all snapshots",
      singleSnapshot: "One snapshot",
      allSnapshots: "All snapshots",
      snapshotLabel: "Snapshot to share",
    },
    errors: {
      invalidCustom: "Pick a date and time in the future.",
      failed: "Could not create the link. Please try again.",
      snapshotRequired: "Pick which snapshot this link should show.",
    },
  },
  deleteDialog: {
    heading: "Delete app",
    subheading:
      "This app and all of its charts and tables will be permanently deleted and cannot be recovered.",
    cancel: "Cancel",
    confirm: "Delete",
    error: "Delete failed. Please try again.",
  },
  deleteSuccess: "App deleted successfully.",
  build: {
    header: "Build {{name}}",
    untitledApp: "New App",
    backAria: "Back to App Studio",
    loadingAria: "Loading app",
    notFound: "App not found.",
    fields: {
      name: "App Name",
      description: "Description",
    },
    validation: {
      nameRequired: "App name is required.",
    },
    errors: {
      loadFailed: "Could not load the app. Please try again.",
      updateFailed: "Could not save changes. Please try again.",
    },
    publish: {
      action: "Publish",
      unpublish: "Unpublish",
      published: "Published",
      draft: "Draft",
    },
    items: {
      empty:
        "Nothing here yet. Add charts or tables from chat results using Add to app.",
      emptyTab:
        "This tab has no widgets yet. Add a chart, table, or KPI card to it.",
      loadingAria: "Loading charts and tables",
      dragHandleAria: "Reorder item",
      addAction: "Add",
      addMenu: {
        chart: "Chart",
        table: "Table",
        kpi: "KPI card",
        tab: "Tab",
        widgets: "Widgets",
        nuggetFeed: "Nugget Feed",
        text: "Text",
      },
      // "Details", not "drill-down": the published view (publishedApps.ts) has always used the
      // plain word with real end users, and the builder saying something else made one feature
      // read as two. The key path stays `drilldown` to match the API field and the
      // `app_widget_drilldowns` table -- jargon is fine in code, just not on screen.
      drilldown: {
        openAria: "Show details for {{name}}",
        // No "Details ·" prefix: the widget's own name is already the title, and the prefix only
        // pushed it out of a narrow dialog header. Matches publishedApps.filteredDialogTitle.
        dialogTitle: "{{name}}",
        filteredDialogTitle: "{{name}} · {{filter}}",
        close: "Close details",
        loading: "Loading details…",
        loadFailed: "Could not load these details. Please try again.",
        unlink: "Unlink",
        deleteChoice: {
          label: "This card has a details view.",
          unlink: "Keep it and show it on the tab",
          delete: "Also delete that widget",
        },
        errors: {
          unlinkFailed: "Could not unlink that widget. Please try again.",
        },
      },
      errors: {
        loadFailed: "Could not load charts and tables. Please try again.",
        reorderFailed: "Could not save the new order. Please try again.",
      },
    },
    charts: {
      sectionTitle: "Charts",
      loadingAria: "Loading charts",
      empty: "No charts yet. Add charts from chat results using Add to app.",
      columnSpan: {
        label: "Width",
        option: "{{span}} of {{total}} columns",
        extraSmall: "Extra Small",
        small: "Small",
        medium: "Medium",
        large: "Large",
        extraLarge: "Extra Large",
        full: "Full",
      },
      errors: {
        loadFailed: "Could not load charts. Please try again.",
        updateFailed: "Could not save chart changes. Please try again.",
      },
    },
    tables: {
      drilldown: {
        cellAria: "Show details for {{value}}, filtered by {{filter}}",
      },
      sectionTitle: "Tables",
      loadingAria: "Loading tables",
      empty: "No tables yet. Add tables from chat results using Add to app.",
      expand: "Expand table",
      close: "Close expanded table",
      expandedFallbackTitle: "Table",
      errors: {
        loadFailed: "Could not load tables. Please try again.",
        updateFailed: "Could not save table changes. Please try again.",
      },
    },
    kpis: {
      loadingAria: "Loading KPI cards",
      empty: "No KPI cards yet.",
      errors: {
        loadFailed: "Could not load KPI cards. Please try again.",
        updateFailed: "Could not save KPI card changes. Please try again.",
      },
    },
    chat: {
      title: "App Builder",
      subtitle:
        "Describe the charts, filters, and layout you want. The assistant will help you build the app step by step.",
      welcome:
        "Hi! Tell me what you want this app to show — metrics, dimensions, filters, and how it should look.",
      inputPlaceholder: "Describe a chart, filter, or layout change…",
      send: "Send",
      openFabAria: "Open app builder chat",
      closeAria: "Close app builder chat",
    },
  },
  newAppModal: {
    title: "New App",
    closeAria: "Close dialog",
    fields: {
      name: "Name",
      description: "Description",
      category: "Category",
      createCategory: "Create new category…",
      newCategory: "New Category Name",
      domain: "Domain",
    },
    actions: {
      cancel: "Cancel",
      addApp: "Add App",
    },
    validation: {
      required: "This field is required.",
    },
    errors: {
      saveFailed: "Could not save the app. Please try again.",
    },
  },
  addToApp: {
    appLabel: "App",
    tabLabel: "Tab",
    selectTabHint: "Select the tab in this app to add to.",
    loadingTabs: "Loading tabs…",
    noApps: "No apps yet. Create an app in App Studio first.",
  },
  addChart: {
    title: "Add chart to app",
    closeAria: "Close dialog",
    description:
      'Add "{{chartTitle}}" to an existing app. The chart will refresh with live data each time you open the app.',
    cancel: "Cancel",
    confirm: "Add to app",
    adding: "Adding…",
    noSql: "No SQL query is available for this chart.",
    errors: {
      saveFailed: "Could not add the chart to the app. Please try again.",
    },
  },
  deleteChart: {
    deleteAria: "Delete chart",
    confirmTitle: "Delete chart?",
    confirmMessage:
      "This chart will be permanently removed from the app. This action cannot be undone.",
    cancel: "Cancel",
    confirm: "Delete",
    deleting: "Deleting…",
    errors: {
      deleteFailed: "Could not delete the chart. Please try again.",
    },
  },
  editChart: {
    openAria: "Edit chart",
  },
  addTable: {
    title: "Add table to app",
    closeAria: "Close dialog",
    description:
      "Add this table to an existing app. The table will refresh with live data each time you open the app.",
    cancel: "Cancel",
    confirm: "Add to app",
    adding: "Adding…",
    noSql: "No SQL query is available for this table.",
    fields: {
      description: "Description (optional)",
      descriptionPlaceholder: "Brief description of what this table shows…",
    },
    errors: {
      saveFailed: "Could not add the table to the app. Please try again.",
    },
  },
  addCards: {
    title: "Add cards to app",
    closeAria: "Close dialog",
    description:
      "Add cards from this answer to an existing app as KPI cards. They will refresh with live data each time you open the app.",
    cardsLabel: "Cards to add",
    cancel: "Cancel",
    confirm: "Add to app",
    adding: "Adding…",
    noSql: "No SQL query is available for these cards.",
    errors: {
      cardRequired: "Select at least one card to add.",
      saveFailed: "Could not add the cards to the app. Please try again.",
    },
  },
  deleteTable: {
    deleteAria: "Delete table",
    confirmTitle: "Delete table?",
    confirmMessage:
      "This table will be permanently removed from the app. This action cannot be undone.",
    cancel: "Cancel",
    confirm: "Delete",
    deleting: "Deleting…",
    errors: {
      deleteFailed: "Could not delete the table. Please try again.",
    },
  },
  editTable: {
    openAria: "Edit table",
  },
  addAppKpi: {
    formatOptions: {
      number: "Number",
      percent: "Percent",
      currency: "Currency",
      text: "Text",
    },
    valueColumn: {
      auto: "Auto (single column)",
    },
  },
  deleteKpi: {
    deleteAria: "Delete KPI card",
    confirmTitle: "Delete KPI card?",
    confirmMessage:
      "This KPI card will be permanently removed from the app. This action cannot be undone.",
    cancel: "Cancel",
    confirm: "Delete",
    deleting: "Deleting…",
    errors: {
      deleteFailed: "Could not delete the KPI card. Please try again.",
    },
  },
  editKpi: {
    openAria: "Edit KPI card",
  },
  addNuggetFeed: {
    title: "Add Nugget Feed",
    closeAria: "Close dialog",
    description:
      "Pick a timeframe. Nuggets from this app's domain created in that window are linked to this widget.",
    fields: {
      timeframe: "Timeframe",
    },
    cancel: "Cancel",
    confirm: "Add",
    adding: "Adding…",
    errors: {
      saveFailed: "Could not add the Nugget Feed. Please try again.",
    },
  },
  editNuggetFeed: {
    title: "Edit Nugget Feed",
    closeAria: "Close dialog",
    description: "Change the timeframe or tab for this Nugget Feed.",
    resnapshotNotice:
      "Changing the timeframe re-matches nuggets now — any nuggets you removed from this feed will be re-added if they still fall in the new window.",
    cancel: "Cancel",
    confirm: "Save",
    saving: "Saving…",
    errors: {
      saveFailed: "Could not save the Nugget Feed. Please try again.",
    },
  },
  nuggetFeed: {
    timeframeOptions: {
      last_1_day: "Last 1 day",
      last_7_days: "Last 7 days",
      last_1_month: "Past 1 month",
      last_calendar_week: "Last calendar week",
      last_calendar_month: "Last calendar month",
    },
    empty: "No nuggets matched this timeframe.",
    listAriaLabel: "Nugget feed",
    editAria: "Edit Nugget Feed",
    deleteAria: "Delete Nugget Feed",
    deleteConfirmTitle: "Delete Nugget Feed?",
    deleteConfirmMessage:
      "This Nugget Feed widget will be permanently removed from the app. The nuggets themselves are not affected. This action cannot be undone.",
    deleteCancel: "Cancel",
    deleteConfirm: "Delete",
    errors: {
      updateFailed: "Could not update the Nugget Feed. Please try again.",
      deleteFailed: "Could not delete the Nugget Feed. Please try again.",
      removeNuggetFailed: "Could not remove the nugget from this feed. Please try again.",
    },
  },
  addTextWidget: {
    title: "Add text",
    closeAria: "Close dialog",
    description: "Write a note, an explanation, or a short summary for this app.",
    cancel: "Cancel",
    confirm: "Add",
    adding: "Adding…",
    errors: {
      saveFailed: "Could not add the text widget. Please try again.",
    },
  },
  editTextWidget: {
    title: "Edit text",
    closeAria: "Close dialog",
    description: "Change the heading, the text, or where this widget sits.",
    cancel: "Cancel",
    confirm: "Save",
    saving: "Saving…",
    errors: {
      saveFailed: "Could not save the text widget. Please try again.",
    },
  },
  textWidget: {
    fields: {
      heading: "Heading (optional)",
      content: "Text",
      // The whole authoring model, in one line. There is no markup to learn: any other line is a
      // paragraph, and line breaks are kept as typed.
      contentHelp: "Start a line with - for a bullet point.",
    },
    empty: "This text widget is empty.",
    editAria: "Edit text widget",
    deleteAria: "Delete text widget",
    deleteConfirmTitle: "Delete text widget?",
    deleteConfirmMessage:
      "This text widget and its content will be permanently removed from the app. This action cannot be undone.",
    deleteCancel: "Cancel",
    deleteConfirm: "Delete",
    errors: {
      updateFailed: "Could not update the text widget. Please try again.",
      deleteFailed: "Could not delete the text widget. Please try again.",
    },
  },
  tabs: {
    selectLabel: "Tab",
    deleteAria: "Delete tab {{name}}",
    errors: {
      tabRequired: "Select a tab for this widget.",
      renameFailed: "Could not rename the tab. Please try again.",
      reorderFailed: "Could not save the new tab order. Please try again.",
      deleteFailed: "Could not delete the tab. Please try again.",
    },
  },
  addAppTab: {
    title: "Add tab",
    closeAria: "Close dialog",
    description:
      "Name the tab. Creating the first tab moves all existing widgets into it; new widgets are then added to a tab you choose.",
    fields: {
      name: "Tab name",
    },
    cancel: "Cancel",
    save: "Add tab",
    saving: "Saving…",
    errors: {
      nameRequired: "Tab name is required.",
      saveFailed: "Could not add the tab. Please try again.",
    },
  },
  snapshots: {
    menuLabel: "Snapshots",
    rename: "Rename",
    delete: "Delete",
    empty: "This app has no snapshots yet.",
  },
  deleteAppSnapshot: {
    title: "Delete snapshot",
    closeAria: "Close dialog",
    description: "Choose a snapshot to delete.",
    chooseAria: "Choose a snapshot to delete",
    warningPinned_one:
      "{{count}} active share link shows only this snapshot. It will stop working.",
    warningPinned_other:
      "{{count}} active share links show only this snapshot. They will stop working.",
    irreversible: "Deleting a snapshot permanently removes its captured data. This cannot be undone.",
    cancel: "Cancel",
    confirm: "Delete",
    errors: {
      deleteFailed: "Could not delete the snapshot. Please try again.",
    },
  },
  renameAppSnapshot: {
    title: "Rename snapshot",
    closeAria: "Close dialog",
    description: "Choose a snapshot to rename.",
    chooseAria: "Choose a snapshot to rename",
    nameLabel: "Snapshot name",
    cancel: "Cancel",
    confirm: "Rename",
    errors: {
      renameFailed: "Could not rename the snapshot. Please try again.",
    },
  },
  captureSnapshot: {
    // The toolbar button is now `snapshots.menuLabel`; this is the menu item under it.
    menu: "Capture new",
    title: "Capture snapshot",
    closeAria: "Close dialog",
    description:
      "Save this app exactly as it looks in the published app view. The snapshot keeps its own copy of every widget's data, so later edits to the app never change it.",
    fields: {
      name: "Snapshot name",
    },
    runningHint: "Running every widget's query. This can take up to a minute — keep this open.",
    cancel: "Cancel",
    save: "Capture",
    saving: "Capturing…",
    errors: {
      nameRequired: "Snapshot name is required.",
      saveFailed: "Could not capture the snapshot. Please try again.",
    },
  },
  deleteAppTab: {
    confirmTitle: 'Delete tab "{{name}}"?',
    confirmMessageWithFallback:
      "Choose where this tab's widgets should move. The tab will then be permanently deleted.",
    confirmMessageLastTab:
      "This is the last tab. Deleting it keeps all widgets and returns the app to its no-tabs layout.",
    fallbackLabel: "Move widgets to",
    cancel: "Cancel",
    confirm: "Delete tab",
  },
  savedQuery: {
    toggle: "Use a saved query",
    selectLabel: "Saved query",
    referenceCount: "Referenced by {{count}} widget(s)",
    empty: "No saved queries yet. Author a query below or save one first.",
    errors: {
      loadFailed: "Could not load saved queries. Please try again.",
    },
  },
  editWidget: {
    title: "Edit widget(s)",
    closeAria: "Close dialog",
    query: {
      description:
        "Edit the query that feeds this widget, then adjust how the widget presents its result. Changes to the query apply to every widget that reuses it.",
      loading: "Loading query…",
    },
    linked: {
      heading: "Linked widgets",
      description:
        "These widgets also reuse this query, so your query changes affect them too. Adjust them here before saving.",
    },
    drilldown: {
      // Shared by the card and table sections so the two read as one feature: one `heading`, one
      // `hint`, one `targetLabel`, and descriptions that differ only in what is clicked. Keep them
      // as single keys rather than a per-section copy — a duplicate is how the two drifted apart
      // before (the table section used to say "Drill-downs" and never mentioned the hiding).
      heading: "Details view",
      description: "Clicking this card opens the chosen widget.",
      enable: "Make this card clickable",
      hint: "The chosen widget is hidden from the tab and opens in a dialog instead.",
      noCandidates:
        "Add a chart or table to this tab first, then pick it here.",
      targetLabel: "Opens widget",
      tabLocked: "Moves with the widget that opens it",
      untitledChart: "Untitled chart",
      untitledTable: "Untitled table",
      table: {
        description:
          "Clicking a cell opens the chosen widget, filtered by that row.",
        add: "Add details view",
        removeAria: "Remove this details view",
        triggerLabel: "Clickable column",
        valueLabel: "Filter by column",
        valueHint: "The clicked row's value in this column filters the widget.",
        empty: "No details views yet.",
        noColumns:
          "Run this table once so its columns are known, then add a details view.",
        // Shown appended to an already-taken column in the picker, so it reads
        // "Students_Pass — Already used" and needs no noun of its own.
        columnTaken: "Already used",
        noSharedColumn:
          "This table and the chosen widget report no column in common, so there is nothing to filter by. Pick a different widget.",
        incomplete:
          "Finish every details view before saving: each one needs a widget, a clickable column and a filter column.",
      },
    },
    json: {
      apply: "Apply",
      heading: "Advanced configuration (JSON)",
      errors: {
        invalidJson: "Must be valid JSON.",
        invalidChart:
          "Chart definition needs a string id and recommended_chart_type.",
        invalidDisplayConfig:
          "Display config must have a columns array of objects with a key.",
        invalidKpiConfig:
          "KPI config must have a format of number, percent, currency, or text.",
      },
    },
    actions: {
      cancel: "Cancel",
      save: "Save changes",
      saving: "Saving…",
    },
    errors: {
      noQuery:
        "No query is associated with this widget, so it can't be edited here.",
      queryLoadFailed: "Could not load the query. Please try again.",
      saveFailed: "Could not save your changes. Please try again.",
    },
  },
  addWidgets: {
    title: "Add widgets",
    closeAria: "Close dialog",
    query: {
      description:
        "Author one query that feeds every widget you add — charts, tables, and KPI cards all read from its result.",
      sectionHeading: "Query",
      queryTabDescription:
        "Write the SQL query. Every widget you compose reads from its result.",
      fields: {
        sql: "SQL query",
      },
      inputMode: {
        aria: "Query input mode",
        query: "Query",
        form: "Form",
        prompt: "Prompt",
      },
      errors: {
        sqlRequired: "SQL query is required.",
        queryRequired: "Select a saved query.",
      },
    },
    results: {
      heading: "Query & results",
      sqlLabel: "Query SQL",
      run: "Run query",
      refresh: "Refresh",
      loading: "Running query…",
      empty: "The query returned no rows.",
      errors: {
        failed: "Could not run the query. Please try again.",
      },
    },
    widgets: {
      heading: "Widgets",
      hint: "Provide and run a query above, then compose widgets from its result.",
    },
    compose: {
      description:
        "Add one or more widgets. Each renders live from the query result below.",
      addWidget: "Add another widget",
      removeAria: "Remove widget",
      kindLabel: "Widget",
      kind: {
        chart: "Chart",
        table: "Table",
        kpi: "KPI card",
      },
      descriptionLabel: "Description (optional)",
      descriptionPlaceholder: "Brief description of what this table shows…",
      kpiNameLabel: "Name",
      kpiNamePlaceholder: "e.g. Total revenue",
      kpiFormatLabel: "Format",
      kpiValueColumnLabel: "Value column",
      kpiPreviewName: "KPI preview",
      columnSpanLabel: "Width",
      table: {
        columnsLabel: "Columns",
        columnsSelected: "{{count}} of {{total}} shown",
        hint: "Drag column headers to reorder, rename inline, and click the arrow to sort.",
        columnNameLabel: "Column display name",
        dragAria: "Reorder column",
        sortAria: "Toggle sort",
        empty: "No columns selected.",
      },
      chart: {
        dimensionLabel: "Dimension (x-axis)",
        measuresLabel: "Measures",
        measuresSelected: "{{count}} of {{total}} shown",
        legendHint:
          "Rename each legend inline, and drag to reorder when more than one is shown.",
        legendNameLabel: "Legend name",
        dragAria: "Reorder legend",
        empty: "Select at least one measure to plot.",
      },
      errors: {
        widgetRequired: "Add at least one widget.",
        kpiNameRequired: "KPI cards need a name.",
      },
    },
    actions: {
      cancel: "Cancel",
      save: "Add widgets",
      saving: "Adding…",
    },
    errors: {
      saveFailed: "Could not add the widgets. Please try again.",
    },
  },
  promptBuilder: {
    emptyState: {
      widgets:
        "Describe the widgets you want — charts, tables, or KPI cards. The query and widgets are generated for you, and you can refine them with follow-up prompts.",
      chart:
        "Describe the chart you want. The SQL query and chart will be generated for you, and you can refine them with follow-up prompts.",
      table:
        "Describe the table you want. The SQL query and table will be generated for you, and you can refine them with follow-up prompts.",
      kpi: "Describe the KPI you want. The query must produce a single aggregated value, and you can refine it with follow-up prompts.",
    },
    placeholder: {
      widgets:
        "Prompt your widgets — describe what to show, or ask for a change…",
      chart: "Prompt your widget — describe what to show, or ask for a change…",
      table:
        "Prompt your table — describe the data to show, or ask for a change…",
      kpi: "Prompt your KPI — describe the value to track, or ask for a change…",
    },
    sendAria: "Send prompt",
    stopAria: "Stop generating",
    transcriptAria: "Prompt conversation",
    generating: "Generating…",
    sqlHeading: "Generated SQL",
    kpiPreviewName: "KPI preview",
    resultRequired: "Generate a result with a prompt before saving.",
    warnings: {
      no_chart_generated:
        "A chart couldn't be generated for this result — showing the data instead.",
      kpi_shape_invalid:
        "The query didn't return a single value (1 row, 1 column). Refine your prompt to ask for one aggregated value.",
      no_sql_generated:
        "No SQL query was generated. Try describing the data you want to see.",
      no_widgets_generated:
        "No widgets could be generated for this result. Try rephrasing what you want to see.",
      kpi_value_column_invalid:
        "A KPI card was skipped because its value column couldn't be resolved. Name the column to display.",
    },
    errors: {
      turnFailed: "Could not generate a result. Please try again.",
    },
    discardDialog: {
      title: "Discard this draft?",
      messageResult:
        "Your generated widget and prompt history haven't been saved. If you discard now, they'll be lost — click \"Save changes\" instead to keep this widget.",
      messageGenerating:
        "A result is still being generated and hasn't been saved. If you discard now, the work in progress will be lost.",
      keepEditing: "Keep editing",
      discard: "Discard draft",
    },
  },
} as const;
